import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Cylinder inventory hooks — read orders.v_cylinder_inventory and
 * orders.v_cylinder_transactions, which derive the customer's live
 * cylinder count from the latest BTRF-* monthly rental invoice plus
 * deliveries / pickups since.
 */

export interface CylinderInventoryRow {
  label: string;
  btrfQboInvoiceId: string | null;
  btrfDocNumber: string | null;
  btrfDate: string | null;
  balanceAtBtrf: number;
  unitPrice: number;
  deliveriesSince: number;
  pickupsSince: number;
  /** Computed: balanceAtBtrf + deliveriesSince - pickupsSince. */
  currentBalance: number;
  /** Computed: currentBalance * unitPrice. */
  estimatedCharge: number;
}

interface InventoryDbRow {
  label: string;
  btrf_qbo_invoice_id: string | null;
  btrf_doc_number: string | null;
  btrf_date: string | null;
  balance_at_btrf: number;
  unit_price: number | string;
  deliveries_since: number;
  pickups_since: number;
}

export function useCylinderInventory() {
  return useQuery<CylinderInventoryRow[]>({
    queryKey: ['cylinders', 'inventory'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_cylinder_inventory')
        .select(
          'label, btrf_qbo_invoice_id, btrf_doc_number, btrf_date, balance_at_btrf, unit_price, deliveries_since, pickups_since',
        )
        .order('label', { ascending: true });
      if (error) return [];
      return (data ?? []).map((r) => {
        const row = r as InventoryDbRow;
        const unit = Number(row.unit_price ?? 0);
        const current =
          row.balance_at_btrf + row.deliveries_since - row.pickups_since;
        return {
          label: row.label,
          btrfQboInvoiceId: row.btrf_qbo_invoice_id,
          btrfDocNumber: row.btrf_doc_number,
          btrfDate: row.btrf_date,
          balanceAtBtrf: row.balance_at_btrf,
          unitPrice: unit,
          deliveriesSince: row.deliveries_since,
          pickupsSince: row.pickups_since,
          currentBalance: current,
          estimatedCharge: current * unit,
        };
      });
    },
    staleTime: 60 * 1000,
  });
}

export interface CylinderTransaction {
  label: string;
  kind: 'start' | 'delivery' | 'pickup' | 'adjustment' | 'system_adjustment';
  qboInvoiceId: string | null;
  docNumber: string | null;
  txnDate: string;
  quantity: number;
}

interface TransactionDbRow {
  label: string;
  kind: 'start' | 'delivery' | 'pickup' | 'adjustment' | 'system_adjustment';
  qbo_invoice_id: string | null;
  doc_number: string | null;
  txn_date: string;
  quantity: number;
}

export function useCylinderTransactions() {
  return useQuery<CylinderTransaction[]>({
    queryKey: ['cylinders', 'transactions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_cylinder_transactions')
        .select('label, kind, qbo_invoice_id, doc_number, txn_date, quantity')
        .order('txn_date', { ascending: true });
      if (error) return [];
      return (data ?? []).map((r) => {
        const row = r as TransactionDbRow;
        return {
          label: row.label,
          kind: row.kind,
          qboInvoiceId: row.qbo_invoice_id,
          docNumber: row.doc_number,
          txnDate: row.txn_date,
          quantity: row.quantity,
        };
      });
    },
    staleTime: 60 * 1000,
  });
}
